import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  AccountCategoryState,
  AccountCategoryListResponse,
  AccountCategoryAttributesResponse,
  AccountCategoryRequestParams,
  AccountCategoryAttributesRequestParams,
  MarketplaceType,
  AccountCategory,
  AccountCategoryAttribute,
} from '../types/AccountCategory';
import AccountCategoryService from '../services/accountCategoryService';

// Helper to create parent key for hierarchical storage
export const createParentKey = (marketplace: string, parentId: string | null): string =>
  `${marketplace}:${parentId === null ? 'root' : parentId}`;

// Initial state for account category management
const initialState: AccountCategoryState = {
  categories: {},
  categoriesByParent: {},
  attributes: {},
  loading: false,
  loadingByParent: {},
  attributesLoading: {},
  error: null,
  errorByParent: {},
  attributesError: {},
  lastFetched: {},
  lastFetchedByParent: {},
};

// Async thunk for fetching account categories
export const fetchAccountCategories = createAsyncThunk(
  'accountCategory/fetchCategories',
  async (params: AccountCategoryRequestParams) => {
    const service = AccountCategoryService.getInstance();
    const { marketplace, search, page, limit, useCache } = params;
    
    const response = await service.getAccountCategories(marketplace, {
      search,
      page,
      limit,
      useCache,
    });
    
    return {
      marketplace,
      response,
    };
  }
);

// Async thunk for fetching category attributes
export const fetchAccountCategoryAttributes = createAsyncThunk(
  'accountCategory/fetchAttributes',
  async (params: AccountCategoryAttributesRequestParams) => {
    const service = AccountCategoryService.getInstance();
    const { marketplace, categoryId } = params;
    
    const response = await service.getAccountCategoryAttributes(marketplace, categoryId);
    
    return {
      categoryId,
      response,
    };
  }
);

// Async thunk for searching categories across marketplaces
export const searchAccountCategories = createAsyncThunk(
  'accountCategory/searchCategories',
  async (params: { searchTerm: string; marketplaces?: MarketplaceType[] }) => {
    const service = AccountCategoryService.getInstance();
    const { searchTerm, marketplaces } = params;
    
    const results = await service.searchAccountCategories(searchTerm, marketplaces);
    
    return {
      searchTerm,
      results,
    };
  }
);

// Async thunk for getting all categories for a marketplace
export const fetchAllAccountCategories = createAsyncThunk(
  'accountCategory/fetchAllCategories',
  async (params: { marketplace: MarketplaceType; search?: string }) => {
    const service = AccountCategoryService.getInstance();
    const { marketplace, search } = params;

    const response = await service.getAllAccountCategories(marketplace, search);

    return {
      marketplace,
      response,
    };
  }
);

// Async thunk for fetching categories by parent (for lazy loading)
export const fetchCategoriesByParent = createAsyncThunk(
  'accountCategory/fetchCategoriesByParent',
  async (params: { marketplace: MarketplaceType; parentId: string | null }) => {
    const service = AccountCategoryService.getInstance();
    const { marketplace, parentId } = params;

    const response = parentId === null
      ? await service.getRootCategories(marketplace)
      : await service.getChildCategories(marketplace, parentId);

    return {
      marketplace,
      parentId,
      parentKey: createParentKey(marketplace, parentId),
      response,
    };
  }
);

// Account category slice
const accountCategorySlice = createSlice({
  name: 'accountCategory',
  initialState,
  reducers: {
    // Clear categories for a specific marketplace
    clearAccountCategoriesForMarketplace: (state, action: PayloadAction<string>) => {
      const marketplace = action.payload;
      delete state.categories[marketplace];
      delete state.lastFetched[marketplace];
      // Clear hierarchical categories for this marketplace
      Object.keys(state.categoriesByParent).forEach(key => {
        if (key.startsWith(`${marketplace}:`)) {
          delete state.categoriesByParent[key];
          delete state.loadingByParent[key];
          delete state.errorByParent[key];
          delete state.lastFetchedByParent[key];
        }
      });
    },

    // Clear all categories
    clearAccountCategories: (state) => {
      state.categories = {};
      state.categoriesByParent = {};
      state.attributes = {};
      state.lastFetched = {};
      state.lastFetchedByParent = {};
      state.error = null;
      state.errorByParent = {};
      state.attributesError = {};
    },

    // Clear errors
    clearAccountCategoryError: (state) => {
      state.error = null;
    },

    // Clear attribute errors for a specific category
    clearAccountCategoryAttributesError: (state, action: PayloadAction<string>) => {
      const categoryId = action.payload;
      delete state.attributesError[categoryId];
    },

    // Clear all attribute errors
    clearAllAccountCategoryAttributesErrors: (state) => {
      state.attributesError = {};
    },

    // Set categories for a marketplace (useful for manual updates)
    setAccountCategoriesForMarketplace: (
      state,
      action: PayloadAction<{ marketplace: string; categories: AccountCategory[] }>
    ) => {
      const { marketplace, categories } = action.payload;
      state.categories[marketplace] = categories;
      state.lastFetched[marketplace] = Date.now();
    },

    // Set attributes for a category (useful for manual updates)
    setAccountCategoryAttributes: (
      state,
      action: PayloadAction<{ categoryId: string; attributes: AccountCategoryAttribute[] }>
    ) => {
      const { categoryId, attributes } = action.payload;
      state.attributes[categoryId] = attributes;
      delete state.attributesLoading[categoryId];
      delete state.attributesError[categoryId];
    },

    // Clear cache for service (triggers service cache clear)
    clearAccountCategoryCache: (state, action: PayloadAction<string | undefined>) => {
      const marketplace = action.payload;
      // This reducer doesn't directly clear cache but can be used to trigger side effects
      // The actual cache clearing happens in the service
      AccountCategoryService.getInstance().clearCache(marketplace);
    },
  },
  extraReducers: (builder) => {
    // Fetch account categories
    builder
      .addCase(fetchAccountCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccountCategories.fulfilled, (state, action) => {
        state.loading = false;
        const { marketplace, response } = action.payload;
        state.categories[marketplace] = response.data.categories;
        state.lastFetched[marketplace] = Date.now();
        state.error = null;
      })
      .addCase(fetchAccountCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch account categories';
      });

    // Fetch account category attributes
    builder
      .addCase(fetchAccountCategoryAttributes.pending, (state, action) => {
        const categoryId = action.meta.arg.categoryId;
        state.attributesLoading[categoryId] = true;
        delete state.attributesError[categoryId];
      })
      .addCase(fetchAccountCategoryAttributes.fulfilled, (state, action) => {
        const { categoryId, response } = action.payload;
        state.attributes[categoryId] = response.data.attributes;
        state.attributesLoading[categoryId] = false;
        delete state.attributesError[categoryId];
      })
      .addCase(fetchAccountCategoryAttributes.rejected, (state, action) => {
        const categoryId = action.meta.arg.categoryId;
        state.attributesLoading[categoryId] = false;
        state.attributesError[categoryId] = action.error.message || 'Failed to fetch category attributes';
      });

    // Search account categories
    builder
      .addCase(searchAccountCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAccountCategories.fulfilled, (state, action) => {
        state.loading = false;
        const { results } = action.payload;
        
        // Update categories for each marketplace in the results
        Object.entries(results).forEach(([marketplace, response]) => {
          state.categories[marketplace] = response.data.categories;
          state.lastFetched[marketplace] = Date.now();
        });
        
        state.error = null;
      })
      .addCase(searchAccountCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search account categories';
      });

    // Fetch all account categories
    builder
      .addCase(fetchAllAccountCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllAccountCategories.fulfilled, (state, action) => {
        state.loading = false;
        const { marketplace, response } = action.payload;
        state.categories[marketplace] = response.data.categories;
        state.lastFetched[marketplace] = Date.now();
        state.error = null;
      })
      .addCase(fetchAllAccountCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch all account categories';
      });

    // Fetch categories by parent (hierarchical loading)
    builder
      .addCase(fetchCategoriesByParent.pending, (state, action) => {
        const parentKey = createParentKey(action.meta.arg.marketplace, action.meta.arg.parentId);
        state.loadingByParent[parentKey] = true;
        delete state.errorByParent[parentKey];
      })
      .addCase(fetchCategoriesByParent.fulfilled, (state, action) => {
        const { parentKey, response } = action.payload;
        state.categoriesByParent[parentKey] = response.data.categories;
        state.loadingByParent[parentKey] = false;
        state.lastFetchedByParent[parentKey] = Date.now();
        delete state.errorByParent[parentKey];
      })
      .addCase(fetchCategoriesByParent.rejected, (state, action) => {
        const parentKey = createParentKey(action.meta.arg.marketplace, action.meta.arg.parentId);
        state.loadingByParent[parentKey] = false;
        state.errorByParent[parentKey] = action.error.message || 'Failed to fetch categories';
      });
  },
});

// Export actions
export const {
  clearAccountCategoriesForMarketplace,
  clearAccountCategories,
  clearAccountCategoryError,
  clearAccountCategoryAttributesError,
  clearAllAccountCategoryAttributesErrors,
  setAccountCategoriesForMarketplace,
  setAccountCategoryAttributes,
  clearAccountCategoryCache,
} = accountCategorySlice.actions;

// Export reducer
export default accountCategorySlice.reducer;

// Selectors for easy access to account category state
export const selectAccountCategoryState = (state: { accountCategory: AccountCategoryState }) =>
  state.accountCategory;

export const selectAccountCategories = (state: { accountCategory: AccountCategoryState }) =>
  state.accountCategory.categories;

export const selectAccountCategoriesLoading = (state: { accountCategory: AccountCategoryState }) =>
  state.accountCategory.loading;

export const selectAccountCategoriesError = (state: { accountCategory: AccountCategoryState }) =>
  state.accountCategory.error;

export const selectAccountCategoryAttributes = (state: { accountCategory: AccountCategoryState }) =>
  state.accountCategory.attributes;

export const selectAccountCategoryAttributesLoading = (state: { accountCategory: AccountCategoryState }) =>
  state.accountCategory.attributesLoading;

export const selectAccountCategoryAttributesError = (state: { accountCategory: AccountCategoryState }) =>
  state.accountCategory.attributesError;

export const selectAccountCategoryLastFetched = (state: { accountCategory: AccountCategoryState }) =>
  state.accountCategory.lastFetched;

// Derived selectors
export const selectAccountCategoriesByMarketplace = (marketplace?: string) => 
  (state: { accountCategory: AccountCategoryState }) => {
    if (!marketplace) return [];
    return state.accountCategory.categories[marketplace] || [];
  };

export const selectAccountCategoryAttributesByCategoryId = (categoryId?: string) =>
  (state: { accountCategory: AccountCategoryState }) => {
    if (!categoryId) return [];
    return state.accountCategory.attributes[categoryId] || [];
  };

export const selectAccountCategoryAttributesLoadingByCategoryId = (categoryId?: string) =>
  (state: { accountCategory: AccountCategoryState }) => {
    if (!categoryId) return false;
    return state.accountCategory.attributesLoading[categoryId] || false;
  };

export const selectAccountCategoryAttributesErrorByCategoryId = (categoryId?: string) =>
  (state: { accountCategory: AccountCategoryState }) => {
    if (!categoryId) return null;
    return state.accountCategory.attributesError[categoryId] || null;
  };

// Check if categories data is stale (older than 5 minutes as per API cache)
export const selectIsAccountCategoriesDataStale = (marketplace?: string) =>
  (state: { accountCategory: AccountCategoryState }) => {
    if (!marketplace || !state.accountCategory.lastFetched[marketplace]) return true;
    const now = Date.now();
    const STALE_THRESHOLD = 24 * 60 * 60 * 1000; // 24 hours
    return (now - state.accountCategory.lastFetched[marketplace]) > STALE_THRESHOLD;
  };

// Get categories as options for UI components
export const selectAccountCategoriesAsOptions = (marketplace?: string) =>
  (state: { accountCategory: AccountCategoryState }) => {
    if (!marketplace) return [];
    const categories = state.accountCategory.categories[marketplace] || [];
    return categories.map(category => ({
      value: category.id,
      label: category.name,
      marketplace,
      parentId: category.parentCategoryId,
    }));
  };

// Selectors for hierarchical categories
export const selectCategoriesByParent = (marketplace: string, parentId: string | null) =>
  (state: { accountCategory: AccountCategoryState }) => {
    const key = createParentKey(marketplace, parentId);
    return state.accountCategory.categoriesByParent[key] || [];
  };

export const selectCategoriesByParentLoading = (marketplace: string, parentId: string | null) =>
  (state: { accountCategory: AccountCategoryState }) => {
    const key = createParentKey(marketplace, parentId);
    return state.accountCategory.loadingByParent[key] || false;
  };

export const selectCategoriesByParentError = (marketplace: string, parentId: string | null) =>
  (state: { accountCategory: AccountCategoryState }) => {
    const key = createParentKey(marketplace, parentId);
    return state.accountCategory.errorByParent[key] || null;
  };

export const selectHasCategoriesByParent = (marketplace: string, parentId: string | null) =>
  (state: { accountCategory: AccountCategoryState }) => {
    const key = createParentKey(marketplace, parentId);
    return key in state.accountCategory.categoriesByParent;
  };